// @givmo/board — download a meeting's calendar file.
//
// The backend generates the iCalendar file from the stored row each time it is asked for, so a
// rescheduled meeting's file carries the new instant and a higher sequence number — which is what
// makes a calendar application UPDATE the entry it already has instead of adding a second one. That is
// why this fetches on demand rather than caching a file the officer downloaded earlier.
//
// The response is `text/plain`, so the generated client parses it as text (its `parseAs` is `auto`),
// and the api-client's envelope middleware leaves non-JSON bodies untouched.

import type { BoardOperations } from '../../board-operations';

/** A filename a calendar application and a human will both accept. */
export function calendarFileName(title: string): string {
  const slug = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${slug.length > 0 ? slug : 'board-meeting'}.ics`;
}

export interface DownloadCalendarOptions {
  orgRef: string;
  meetingId: string;
  title: string;
}

/**
 * Fetch the meeting's `.ics` and hand it to the browser as a download.
 *
 * Throws on failure so the caller can surface it — a silent no-op would leave an officer clicking a
 * button that appears to do nothing.
 */
export async function downloadMeetingCalendar(
  options: DownloadCalendarOptions,
  operations: BoardOperations,
): Promise<void> {
  const { orgRef, meetingId, title } = options;
  const { data } = await operations.officerGetBoardMeetingCalendar({
    path: { org_ref: orgRef, meeting_id: meetingId },
    throwOnError: true,
  });
  saveCalendarFile(data, title);
}

/**
 * Hand a fetched calendar body to the browser as a download. Exported for the director door's own
 * fetch, which lives with the director pages and saves the same file.
 */
export function saveCalendarFile(data: unknown, title: string): void {
  const blob = new Blob([typeof data === 'string' ? data : String(data)], {
    type: 'text/calendar;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = calendarFileName(title);
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Release the object URL on the next tick — revoking synchronously can race the click in some
    // browsers and cancel the download.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
